import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useConfig } from "@/context/ConfigContext";
import { useLayout, HEADER_FALLBACK, isInternal } from "@/context/LayoutContext";
import { fileUrl } from "@/lib/api";

// One-level dropdown support for CMS-managed header nav. An item is a dropdown when it has
// at least one active child; otherwise it renders as a standard link (empty-dropdown fallback).
const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const isInternalDest = (h) => typeof h === "string" && (h.startsWith("/") || h.startsWith("#"));
const activeChildren = (item) => ((item && item.children) || []).filter((c) => c && c.active !== false && (c.href || "").trim());
const isDropdown = (item) => activeChildren(item).length > 0;

function NavDropdown({ item, overHero }) {
  const [hoverOpen, setHoverOpen] = useState(false);
  const [clickOpen, setClickOpen] = useState(false);
  const open = hoverOpen || clickOpen;
  const wrapRef = useRef(null), btnRef = useRef(null), itemRefs = useRef([]);
  const kids = activeChildren(item);
  const slug = slugify(item.label);
  const close = () => { setHoverOpen(false); setClickOpen(false); };
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) close(); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const focusItem = (i) => { const n = kids.length; if (!n) return; itemRefs.current[(i + n) % n]?.focus(); };
  const onParentKey = (e) => {
    // Enter/Space fire a native click on the button (handled by onClick); here we only add arrow/escape.
    if (e.key === "ArrowDown") { e.preventDefault(); setClickOpen(true); setTimeout(() => focusItem(0), 0); }
    else if (e.key === "Escape") { close(); }
  };
  const onItemKey = (e, i) => {
    if (e.key === "ArrowDown") { e.preventDefault(); focusItem(i + 1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); focusItem(i - 1); }
    else if (e.key === "Escape") { e.preventDefault(); close(); btnRef.current?.focus(); }
    else if (e.key === "Tab") { close(); }
  };
  const cls = "block px-4 py-2.5 font-accent text-sm text-charcoal outline-none transition-colors hover:bg-saffron/10 hover:text-saffron focus:bg-saffron/10 focus:text-saffron";
  return (
    <div ref={wrapRef} className="relative" onMouseEnter={() => setHoverOpen(true)} onMouseLeave={() => setHoverOpen(false)}>
      <button ref={btnRef} type="button" data-testid={`nav-dropdown-${slug}`} aria-haspopup="menu" aria-expanded={open}
        onClick={() => setClickOpen((o) => (open ? false : true))} onKeyDown={onParentKey}
        className={`flex items-center gap-1 font-accent text-[13px] font-medium tracking-wide transition-colors duration-200 hover:text-saffron ${overHero ? "text-cream" : "text-charcoal"}`}>
        {item.label}<ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div role="menu" aria-label={item.label} data-testid={`nav-dropdown-${slug}-menu`}
          className="absolute left-0 top-full z-50 min-w-[240px] pt-2">
          <div className="rounded-xl border border-gold/20 bg-cream py-2 shadow-lg">
          {kids.map((c, i) => {
            const tid = `nav-dropdown-${slug}-child-${i}`;
            return isInternalDest(c.href) ? (
              <Link key={i} to={c.href} role="menuitem" data-testid={tid} ref={(el) => (itemRefs.current[i] = el)}
                tabIndex={-1} onKeyDown={(e) => onItemKey(e, i)} onClick={close} className={cls}>{c.label}</Link>
            ) : (
              <a key={i} href={c.href} role="menuitem" data-testid={tid} ref={(el) => (itemRefs.current[i] = el)}
                tabIndex={-1} onKeyDown={(e) => onItemKey(e, i)} onClick={close} className={cls}
                {...(c.new_tab ? { target: "_blank", rel: "noopener noreferrer" } : {})}>{c.label}</a>
            );
          })}
          </div>
        </div>
      )}
    </div>
  );
}

function MobileNavDropdown({ item, onNavigate }) {
  const [exp, setExp] = useState(false);
  const kids = activeChildren(item);
  const slug = slugify(item.label);
  return (
    <div className="border-b border-gold/10">
      <button type="button" data-testid={`mobile-nav-dropdown-${slug}`} aria-expanded={exp} onClick={() => setExp((e) => !e)}
        className="flex w-full items-center justify-between py-3 font-accent text-sm text-charcoal">
        {item.label}<ChevronDown className={`h-4 w-4 transition-transform ${exp ? "rotate-180" : ""}`} />
      </button>
      {exp && (
        <div className="pb-2 pl-4" data-testid={`mobile-nav-dropdown-${slug}-submenu`}>
          {kids.map((c, i) => {
            const tid = `mobile-nav-dropdown-${slug}-child-${i}`;
            return isInternalDest(c.href) ? (
              <Link key={i} to={c.href} onClick={onNavigate} data-testid={tid} className="block py-2.5 font-accent text-sm text-charcoal hover:text-saffron">{c.label}</Link>
            ) : (
              <a key={i} href={c.href} onClick={onNavigate} data-testid={tid} className="block py-2.5 font-accent text-sm text-charcoal hover:text-saffron"
                {...(c.new_tab ? { target: "_blank", rel: "noopener noreferrer" } : {})}>{c.label}</a>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hasHero, setHasHero] = useState(false);
  const [isDesktop, setIsDesktop] = useState(typeof window !== "undefined" ? window.innerWidth >= 1024 : true);
  const location = useLocation();
  const config = useConfig();
  const layout = useLayout();
  const hd = layout.header || HEADER_FALLBACK;
  const NAV = (hd.nav_links && hd.nav_links.length ? hd.nav_links : HEADER_FALLBACK.nav_links).map((n) => ({ to: n.href, label: n.label, children: n.children }));
  const cta = hd.cta || HEADER_FALLBACK.cta;
  const showCta = cta && cta.enabled !== false && (cta.label || "").trim() && (cta.href || "").trim();
  const logo = config.logo_url ? fileUrl(config.logo_url) : null;
  const mobileLogo = config.mobile_logo_url ? fileUrl(config.mobile_logo_url) : logo;
  const dW = config.logo_w_desktop || 172, dH = config.logo_h_desktop || 76, hH = config.header_h_desktop || 100;
  const mW = config.logo_w_mobile || 128, mH = config.logo_h_mobile || 56, mHh = config.header_h_mobile || 72;

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  useEffect(() => {
    const detect = () => setHasHero(!!document.querySelector('[data-testid="hero-section"], [data-testid="page-hero"]'));
    detect();
    const obs = new MutationObserver(detect);
    obs.observe(document.body, { childList: true, subtree: true });
    const stop = setTimeout(() => obs.disconnect(), 2000);
    return () => { obs.disconnect(); clearTimeout(stop); };
  }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const overHero = hasHero && !scrolled && !logo; // solid cream header when a brand logo is set (keeps transparent logo readable)

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 z-40 w-full transition-all duration-300 ${
        overHero
          ? "backdrop-blur-md bg-cream/90 border-b border-gold/20 lg:bg-transparent lg:backdrop-blur-0 lg:border-transparent lg:shadow-none"
          : "backdrop-blur-xl bg-cream/90 border-b border-gold/20 shadow-sm"
      }`}
    >
      {overHero && <div className="pointer-events-none absolute inset-0 hidden bg-gradient-to-b from-charcoal/45 to-transparent lg:block" aria-hidden="true" />}
      <div className="relative mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6" style={{ minHeight: isDesktop ? hH : mHh }}>
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5">
          {logo ? (
            <img src={logo} alt="Maha Sampada — Discover Maharashtra's Treasures" onError={(e) => { e.currentTarget.style.display = "none"; }}
              style={{ width: isDesktop ? dW : mW, maxHeight: isDesktop ? dH : mH, height: "auto" }} className="w-auto object-contain" />
          ) : (
            <>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-maroon text-cream font-display text-xl font-bold">M</div>
              <div className="leading-tight">
                <div className={`font-display text-xl font-bold ${overHero ? "text-charcoal lg:text-cream" : "text-charcoal"}`}>Maha Sampada</div>
                <div className={`overline hidden text-[9px] sm:block ${overHero ? "text-gold lg:text-cream/90" : "text-saffron"}`}>Discover Maharashtra's Treasures</div>
              </div>
            </>
          )}
        </Link>

        <nav className="hidden items-center gap-6 lg:flex">
          {NAV.map((n) => (
            isDropdown(n) ? (
              <NavDropdown key={"dd-" + n.label} item={n} overHero={overHero} />
            ) : isInternal(n.to) ? (
              <NavLink
                key={n.to + n.label}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-accent text-[13px] font-medium tracking-wide transition-colors duration-200 hover:text-saffron ${
                    isActive ? "text-saffron" : overHero ? "text-cream" : "text-charcoal"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ) : (
              <a key={n.to + n.label} href={n.to} target="_blank" rel="noopener noreferrer"
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`font-accent text-[13px] font-medium tracking-wide transition-colors duration-200 hover:text-saffron ${overHero ? "text-cream" : "text-charcoal"}`}>
                {n.label}
              </a>
            )
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {showCta && (
            isInternal(cta.href) ? (
              <Link to={cta.href} className="hidden sm:block">
                <Button data-testid="header-query-cta" className="rounded-full bg-saffron px-5 font-accent text-sm font-semibold text-white hover:bg-saffron-dark">
                  {cta.label}
                </Button>
              </Link>
            ) : (
              <a href={cta.href} target="_blank" rel="noopener noreferrer" className="hidden sm:block">
                <Button data-testid="header-query-cta" className="rounded-full bg-saffron px-5 font-accent text-sm font-semibold text-white hover:bg-saffron-dark">
                  {cta.label}
                </Button>
              </a>
            )
          )}
          <button
            data-testid="mobile-menu-toggle"
            className="rounded-md p-2 text-charcoal lg:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="border-t border-gold/20 bg-cream lg:hidden">
          <nav className="flex flex-col px-4 py-4">
            {mobileLogo && <img src={mobileLogo} alt="Maha Sampada" onError={(e) => { e.currentTarget.style.display = "none"; }} className="mb-3 h-12 w-auto max-w-[200px] self-start object-contain" />}
            {NAV.map((n) => (
              isDropdown(n) ? (
                <MobileNavDropdown key={"m-dd-" + n.label} item={n} onNavigate={() => setOpen(false)} />
              ) : isInternal(n.to) ? (
                <NavLink
                  key={n.to + n.label}
                  to={n.to}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `border-b border-gold/10 py-3 font-accent text-sm ${isActive ? "text-saffron" : "text-charcoal"}`
                  }
                >
                  {n.label}
                </NavLink>
              ) : (
                <a key={n.to + n.label} href={n.to} target="_blank" rel="noopener noreferrer"
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="border-b border-gold/10 py-3 font-accent text-sm text-charcoal">
                  {n.label}
                </a>
              )
            ))}
            {showCta && (
              isInternal(cta.href) ? (
                <Link to={cta.href} className="mt-4">
                  <Button data-testid="mobile-query-cta" className="w-full rounded-full bg-saffron font-accent font-semibold text-white hover:bg-saffron-dark">
                    {cta.label}
                  </Button>
                </Link>
              ) : (
                <a href={cta.href} target="_blank" rel="noopener noreferrer" className="mt-4">
                  <Button data-testid="mobile-query-cta" className="w-full rounded-full bg-saffron font-accent font-semibold text-white hover:bg-saffron-dark">
                    {cta.label}
                  </Button>
                </a>
              )
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
